'use client'

import Link from 'next/link'
import { LinkIcon, PhoneIcon, UserIcon } from 'lucide-react'

import { Media } from '@/components/Media'
import { useImgOnloadFallback } from '@/hooks/useImgOnloadFallback'
import { formatLegislatorCardSubtitle } from '@/utilities/billTrack50/formatLegislatorDisplay'
import { cn } from '@/utilities/ui'

import type { Legislator } from '@/payload-types'

const formatContactLink = (
  email: string | null | undefined,
  isEmail: boolean | null | undefined,
) => {
  if (!email) return undefined

  if (isEmail) {
    return email
  }

  try {
    const { hostname } = new URL(email)
    const parts = hostname.replace(/^www\./, '').split('.')

    if (parts.length <= 2) {
      return parts.join('.')
    }

    return parts.slice(-3).join('.')
  } catch {
    return email
  }
}

/**
 * Card for a legislator sponsor on bill detail and related surfaces.
 */
export const PoliticianCard = ({ politician }: { politician: Legislator }) => {
  const name = politician.bt50?.name ?? politician.title
  const role = politician.bt50?.role ?? politician.title
  const subtitle = formatLegislatorCardSubtitle({
    party: politician.bt50?.party,
    role,
    stateCode: politician.bt50?.stateCode,
  })
  const imageURL = politician.billtrackSite?._bt50ImageURL
  const mahaImage = politician._customImage ?? politician.headshot
  const email = politician.billtrackSite?.email
  const phone = politician.billtrackSite?.phone

  const { imageLoaded } = useImgOnloadFallback({
    imageURL: imageURL ?? '',
    override: Boolean(mahaImage),
  })

  const isEmail = Boolean(email && email.includes('@') && email.includes('.'))
  const formattedContactLink = formatContactLink(email, isEmail)

  return (
    <div className="grid size-full max-w-[394px] min-w-0 basis-[394px] grid-cols-[minmax(0,25%)_minmax(0,75%)] items-center gap-3 rounded-xl border border-border bg-white p-3 md:grid-cols-[minmax(0,30%)_minmax(0,70%)] md:px-5 md:py-4">
      <div className="relative flex aspect-square w-full items-center justify-center rounded-full">
        <div
          className={cn(
            'absolute inset-0 flex size-full items-center justify-center rounded-full bg-neutral-200 transition-opacity',
            mahaImage || (imageURL && imageLoaded)
              ? 'pointer-events-none opacity-0'
              : 'opacity-100',
          )}
        >
          <UserIcon className="size-8 text-neutral-400" />
        </div>
        {(mahaImage ?? imageURL) && (
          <Media
            loading="lazy"
            resource={mahaImage ?? imageURL}
            alt=""
            fill
            sizes="110px"
            htmlelement={null}
            pictureClassName={cn(
              'aspect-square size-full overflow-hidden rounded-full object-cover object-top transition-opacity',
              imageLoaded || mahaImage ? 'opacity-100' : 'opacity-0',
            )}
            imgClassName="aspect-square rounded-full object-cover object-top"
          />
        )}
      </div>
      <div className="w-full flex-1 font-body">
        <div className="mb-3">
          <h3 className="text-base font-semibold">{name}</h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex flex-col gap-1">
          {phone && (
            <Link
              href={`tel:${phone}`}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <PhoneIcon className="size-4 shrink-0 text-neutral-400" />
              {phone}
            </Link>
          )}
          {email && (
            <Link
              href={isEmail ? `mailto:${email}` : email}
              title={formattedContactLink}
              target="_blank"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <LinkIcon className="size-4 shrink-0 text-neutral-400" />
              <span className="min-w-0 truncate">{formattedContactLink}</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
